import { useState } from "react"
import { toast } from "sonner"
import { Plus, Copy } from "lucide-react"
import {
  useApiApplications,
  useCreateApiApplication,
  useRotateApiApplication,
  useRevokeApiApplication,
} from "@/lib/queries"
import type { ApiApplicationWithSecret } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog"
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table"

export function ApiAccessPage() {
  const { data: apps, isLoading, isError } = useApiApplications()
  const createApp = useCreateApiApplication()
  const rotateApp = useRotateApiApplication()
  const revokeApp = useRevokeApiApplication()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [name, setName] = useState("")
  const [revealed, setRevealed] = useState<ApiApplicationWithSecret | null>(null)

  // Docs are same-origin on the app host, gated behind the current login session.
  const apiDocsUrl = "/docs"

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const app = await createApp.mutateAsync({ name })
      setRevealed(app)
      setName("")
      toast.success("Application created")
    } catch {
      toast.error("Could not create application")
    }
  }

  const handleRotate = async (id: number) => {
    if (!confirm("Rotate this secret? The current secret stops working immediately.")) return
    try {
      const app = await rotateApp.mutateAsync(id)
      setRevealed(app)
      setDialogOpen(true)
      toast.success("Secret rotated")
    } catch {
      toast.error("Could not rotate secret")
    }
  }

  const handleRevoke = async (id: number) => {
    if (!confirm("Revoke this application? Its tokens stop working within the hour.")) return
    try {
      await revokeApp.mutateAsync(id)
      toast.success("Application revoked")
    } catch {
      toast.error("Could not revoke application")
    }
  }

  const openCreate = () => {
    setRevealed(null)
    setName("")
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
    setRevealed(null)
    setName("")
  }

  return (
    <div className="space-y-6 pt-2">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Create credentials to read your organization's documents via the API.
          </p>
          <a
            href={apiDocsUrl}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-primary underline underline-offset-2"
          >
            View API documentation →
          </a>
        </div>
        <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New application</Button>
        <Dialog open={dialogOpen} onOpenChange={(o) => (o ? setDialogOpen(true) : closeDialog())}>
          <DialogContent>
            {revealed ? (
              <>
                <DialogHeader>
                  <DialogTitle>Copy your client secret</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  This secret is shown only once. Store it somewhere safe now.
                </p>
                <div className="space-y-2">
                  <Label>Client ID</Label>
                  <Input readOnly value={revealed.client_id} />
                  <Label>Client secret</Label>
                  <div className="flex gap-2">
                    <Input readOnly value={revealed.client_secret} />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(revealed.client_secret)
                        toast.success("Copied")
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={closeDialog}>Done</Button>
                </DialogFooter>
              </>
            ) : (
              <form onSubmit={handleCreate}>
                <DialogHeader>
                  <DialogTitle>New application</DialogTitle>
                </DialogHeader>
                <div className="space-y-2 py-4">
                  <Label htmlFor="app-name">Name</Label>
                  <Input
                    id="app-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Prod integration"
                    required
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={createApp.isPending}>
                    {createApp.isPending ? "Creating..." : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5}>Loading...</TableCell></TableRow>
            ) : isError ? (
              <TableRow><TableCell colSpan={5}>You don't have access to API applications.</TableCell></TableRow>
            ) : !apps || apps.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No applications yet.</TableCell></TableRow>
            ) : (
              apps.map((app) => (
                <TableRow key={app.id}>
                  <TableCell>{app.name}</TableCell>
                  <TableCell className="font-mono text-xs">{app.client_id}</TableCell>
                  <TableCell>{app.active ? "Active" : "Revoked"}</TableCell>
                  <TableCell>{app.last_used_at ? new Date(app.last_used_at).toLocaleString() : "Never"}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {app.active && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleRotate(app.id)}>Rotate</Button>
                        <Button size="sm" variant="destructive" onClick={() => handleRevoke(app.id)}>Revoke</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
